import React, { Children, ComponentProps, PropsWithChildren } from 'react';
import { StyleSheet } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { HorizontalScrollRail, HStack, theme, useResponsive } from '@sg/garnish';

import { useHomeContainerSpacing } from '../../hooks';
import { HOME_PRODUCT_CARD_SIZES } from '../HomeProductCard';

// ─────────────────────────────────────────────────────────────────────────────

export const HomeGenericCategory = (props: ComponentProps<typeof HorizontalScrollRail>) => {
  const { children } = props;

  const { currentBreakpoint } = useResponsive();
  const containerStyle = useHomeContainerSpacing();

  if (currentBreakpoint.isXS) {
    return (
      <HorizontalScrollRail
        outerOffset={0}
        showNavControls={false}
        gap={theme.spacing['2']}
        contentContainerStyle={[containerStyle, styles.columnGap]}
      >
        {children}
      </HorizontalScrollRail>
    );
  }

  return <DynamicHStack>{children}</DynamicHStack>;
};

export const HomeFilterableCategory = (props: PropsWithChildren) => {
  const { children } = props;

  const { match } = useResponsive();
  const filterableStyle = match([styles.filterableCategoryXS, styles.filterableCategorySM]);

  return <DynamicHStack style={filterableStyle}>{children}</DynamicHStack>;
};

const DynamicHStack = (props: ComponentProps<typeof HStack>) => {
  const { children, style } = props;

  const { match, screenDimensions } = useResponsive();
  const containerStyle = useHomeContainerSpacing();

  const childrenCount = Children.count(children);
  const shouldHaveOneItemPerRow = childrenCount === 1 || screenDimensions.width < 350;
  const itemsPerRow = shouldHaveOneItemPerRow ? 1 : match([2, 2, 3]);
  const productCardSize = match(HOME_PRODUCT_CARD_SIZES);
  const containerSize = itemsPerRow * (productCardSize + GRID_GAP) + GRID_PADDING * 2;
  const containerSizeStyle = useStyle(() => ({ width: containerSize }), [containerSize]);

  const categoryStyle = match([containerStyle, styles.categoriesSM]);
  const containerStyles = [categoryStyle, match([null, containerSizeStyle]), style];

  return (
    <HStack
      style={containerStyles}
      itemsPerRow={shouldHaveOneItemPerRow ? 1 : match([2, 2, 3])}
      gap={match([theme.spacing['2'], theme.spacing['6']])}
      itemStyle={styles.itemStyle}
    >
      {children}
    </HStack>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const GRID_GAP = theme.spacing['6'];
const GRID_PADDING = theme.spacing['10'];

const styles = StyleSheet.create({
  flexView: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing['2'],
  },
  categoriesSM: {
    width: '100%',
    alignSelf: 'center',
    marginLeft: 0,
    marginRight: 0,
    paddingHorizontal: GRID_PADDING,
    rowGap: theme.spacing['8'],
  },
  filterableCategoryXS: {
    paddingVertical: theme.spacing['6'],
  },
  filterableCategorySM: {
    paddingVertical: theme.spacing['6'],
  },
  columnGap: {
    columnGap: theme.spacing['2'],
  },
  itemStyle: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});
